import { TextCollectionResponse } from '@scale/shared/types/response/textcollection';
import { FieldSetReview } from './review';
import _ from 'lodash';

const localStorageKey = 'textcollection/backups';

// Backups should last 24 hours.
// The maxDuration amount is a function of how long taskers have to finish their task after claiming.
// TODO: The actual claim time is 3 hours. The claim time and localStorage duration should reference the same constant.
const maxDuration = 24 * 60 * 60 * 1000;

const hasLocalStorage = (function () {
  let checked = false;
  let detected = false;
  return function (): boolean {
    if (!process.browser) {
      return false;
    }
    if (!checked) {
      try {
        window.localStorage.setItem('__test', '1');
        window.localStorage.removeItem('__test');
        detected = true;
      } finally {
        checked = true;
      }
    }
    return detected;
  };
})();

interface TextCollectionBackup {
  response: TextCollectionResponse;
  fieldSetReviews: Record<string, Record<number, FieldSetReview>>;
  expired: number;
}

export function getBackup(backupKey: string | undefined): TextCollectionBackup | undefined {
  if (!backupKey) {
    return;
  }
  const backups = getBackups();
  const backup = backups[backupKey];
  if (!backup) {
    return;
  }
  // Guard against older versions.
  if (!backup.fieldSetReviews) {
    backup.fieldSetReviews = {};
  }
  return backup;
}

const getNonExpiredBackups = (
  backups: Record<string, TextCollectionBackup>,
): Record<string, TextCollectionBackup> => {
  const now = Date.now();
  return _.pickBy(backups, backup => {
    return backup.expired >= now;
  });
};

export function deleteBackup(backupKey: string | undefined) {
  if (!backupKey) {
    return;
  }
  const backups = getBackups();
  delete backups[backupKey];
  setBackups(getNonExpiredBackups(backups));
}

export function saveBackup(
  backupKey: string,
  response: TextCollectionResponse,
  fieldSetReviews: Record<string, Record<number, FieldSetReview>>,
): void {
  const now = Date.now();
  const backups = getBackups();
  backups[backupKey] = {
    response,
    fieldSetReviews,
    expired: now + maxDuration,
  };
  setBackups(getNonExpiredBackups(backups));
}

function setBackups(backups: Record<string, TextCollectionBackup>): void {
  if (!hasLocalStorage()) {
    return;
  }
  try {
    localStorage.setItem(localStorageKey, JSON.stringify(backups));
  } catch {
    // Sometimes we run out of storage space
    localStorage.removeItem(localStorageKey);
  }
}

function getBackups(): Record<string, TextCollectionBackup> {
  if (!hasLocalStorage()) {
    return {};
  }
  const item = localStorage.getItem(localStorageKey);
  if (!item) {
    return {};
  }
  try {
    const backups = JSON.parse(item);
    return backups;
  } catch (e) {
    return {};
  }
}
